import { Box, Button, Layout, Text } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { delay } from '../../../utils/delay'
import { RichText } from '../../reusable/RichText'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { Content } from './WixVibeMobileSplit.content'
import { wixVibeMobileSplitDataHooks } from './WixVibeMobileSplit.dataHooks'
import s from './WixVibeMobileSplit.scss'

export const WixVibeMobileSplit: FC = () => {
  const [isShowButtons, setIsShowButtons] = useState(false)
  const { content } = useContent<Content>()
  const pageLogger = usePageLogger()
  const [isPageLoading] = usePageLoading()
  const next = useNext()

  const handleNext = async () => {
    const nextAnswer = 'wix_vibe'
    if (isPageLoading) {
      return
    }

    await pageLogger.generalAction({ action: nextAnswer })
    await pageLogger.answer({ answer: nextAnswer })
    next({ [content.question.questionKey]: { values: [nextAnswer] } })
  }

  const handleSkip = async () => {
    const skipAnswer = 'wix_editor'
    if (isPageLoading) {
      return
    }

    await pageLogger.generalAction({ action: skipAnswer })
    await pageLogger.answer({ answer: skipAnswer })
    next({ [content.question.questionKey]: { values: [skipAnswer] } })
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    const showSkipWithDelay = async () => {
      await delay(100)
      setIsShowButtons(true)
    }
    showSkipWithDelay()
  }, [])

  return (
    <WixMobilePage>
      <WixMobileHeader />
      <div className={s.container}>
        <WixMobileContentWrapper>
          <WixMobileHeading dataHook={wixVibeMobileSplitDataHooks.title()} textAlign="left">
            {content.title}
          </WixMobileHeading>

          <Layout cols={1} gap="8px">
            {content.description && (
              <div style={{ paddingTop: '12px' }}>
                <Text weight="normal" dataHook={wixVibeMobileSplitDataHooks.description()}>
                  {content.description}
                </Text>
              </div>
            )}

            <Box direction="vertical" gap="24px" marginTop="20px">
              {content.question.options.map(option => (
                <RichText
                  weight="thin"
                  id={option.optionKey}
                  dataHook={wixVibeMobileSplitDataHooks.option({ key: option.optionKey })}
                >
                  {option.label}
                </RichText>
              ))}
            </Box>
          </Layout>
        </WixMobileContentWrapper>

        <WixMobileFooter>
          <div className={s.buttons}>
            {content.next && isShowButtons && (
              <Button
                skin="dark"
                size="large"
                fullWidth
                onClick={handleNext}
                dataHook={wixVibeMobileSplitDataHooks.next()}
              >
                {content.next}
              </Button>
            )}

            {content.skip && isShowButtons && (
              <Button
                id={wixVibeMobileSplitDataHooks.skip()}
                skin="dark"
                priority="secondary"
                size="large"
                fullWidth
                onClick={handleSkip}
                dataHook={wixVibeMobileSplitDataHooks.skip()}
              >
                {content.skip}
              </Button>
            )}
          </div>
        </WixMobileFooter>
      </div>
    </WixMobilePage>
  )
}
